import React, { BaseSyntheticEvent, FC, memo, useContext, useEffect } from 'react'

import { useMutation } from '@apollo/client'
import { pick } from 'lodash-es'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useWizardStep } from 'react-wizard-primitive'

import { AppContext, DEFAULT_COUNTRY_ISO_CODE, EMAIL_PATTERN, LOGOUT_CUSTOMER, SIGN_UP_CUSTOMER } from 'Const'
import { useCustomer } from 'Hooks/checkout'
import useEvent from 'Hooks/useEvent'
import useToggle from 'Hooks/useToggle'
import ContactIcon from 'Icons/steps/contact.svg'
import { AccountCustomerAccountsStates, Customer } from 'Types'
import { sendGTMAccountCreatedEvent } from 'Utils/analytics/gtmEvents'
import { noty } from 'Utils/index'

import ErrorMessage from 'Components/ErrorMessage'
import PhoneInput from 'Components/PhoneInput'

import { StepContext } from '..'
import FormErrors from '../FormErrors'
import NextStepBtn from '../NextStepBtn'

import './index.scss'
import ForgotPasswordModal from './ForgotPasswordModal'
import LoginModal from './LoginModal'

export interface FormState {
  email: string
  firstName: string
  lastName: string
  password?: string
  phone: string
  subscribed?: boolean
}

// Checkout Step #1
const CustomerFieldsGroup: FC = () => {
  const { locationCountryIsoCodes, account, currentLocation } = useContext(AppContext)
  const { checkouting, isLastStep, nextStep } = useContext(StepContext)
  const form = useForm<FormState>()
  const { register, control, handleSubmit, errors, setError } = form
  const { isActive, resetToStep } = useWizardStep()
  const customer = useCustomer()
  const requestPassword = customer?.requestPassword
  const hasEnoughValues = customer?.firstName && customer?.lastName && customer?.email && customer?.phone
  const shouldSignUp = !customer || !hasEnoughValues || requestPassword

  const [wantPassword, toggleWantPassword] = useToggle()
  const [loginModalVisibility, , openLoginModal, closeLoginModal] = useToggle()
  const [forgotPasswordModalVisibility, , openForgotPasswordModal, closeForgotPasswordModal] = useToggle()

  const [signUp, { error, loading }] = useMutation(SIGN_UP_CUSTOMER, {
    update(cache, { data }) {
      cache.modify({
        fields: {
          currentCustomer() {
            return {
              ...(data.signUpCustomer.customer as Customer),
              __typename: 'Customer',
            }
          },
        },
      })
    },
    onCompleted: (data) => {
      const customer = data.signUpCustomer.customer as Customer
      if (customer.requestPassword) {
        openLoginModal()
        noty('Email already registered, login instead')
      } else {
        nextStep({ customer: pick(customer, ['firstName', 'lastName', 'email', 'phone']) })
        sendGTMAccountCreatedEvent(customer)
      }
    },
  })

  const [logout] = useMutation(LOGOUT_CUSTOMER, {
    update(cache) {
      cache.modify({
        fields: {
          currentCustomer() {
            return null
          },
        },
      })
    },
  })

  useEffect(() => {
    if (error) {
      error.graphQLErrors.forEach((error) => {
        if (error.extensions?.fieldName) {
          if (error.extensions.accountAlreadyExists) {
            openLoginModal()
          } else {
            setError(error.extensions.fieldName as keyof FormState, { message: error.message })
          }
        }
      })
    }
  }, [error, setError, openLoginModal])

  const handleLogout = useEvent(() => void logout(), [logout])

  const onSubmit = useEvent((e?: BaseSyntheticEvent) => {
    const submit = handleSubmit((input: FormState) => {
      if (shouldSignUp) {
        void signUp({ variables: { input } })
      } else {
        nextStep({ customer: pick(customer, ['firstName', 'lastName', 'email', 'phone']) })
      }
    })

    void submit(e)
  })

  const mainCountryLocationIsoCode = locationCountryIsoCodes[0] || DEFAULT_COUNTRY_ISO_CODE
  const isOptionalLogin = account.customerAccountsState === AccountCustomerAccountsStates.OPTIONAL
  const isDisabledLogin = account.customerAccountsState === AccountCustomerAccountsStates.DISABLED
  const isRequiredLogin = account.customerAccountsState === AccountCustomerAccountsStates.REQUIRED

  return (
    <FormProvider {...form}>
      <div className="checkout-step / mb-5">
        <h2 className="checkout-step__header / h4">
          <ContactIcon />
          <span>Contact</span>
          {shouldSignUp ? null : isActive ? (
            <button className="checkout-button / ml-auto" type="button" onClick={handleLogout}>
              Log out
            </button>
          ) : (
            <button className="checkout-button / ml-auto" type="button" onClick={resetToStep}>
              Change
            </button>
          )}
        </h2>
        {shouldSignUp ? (
          <form hidden={!isActive && !shouldSignUp}>
            {isDisabledLogin ? null : (
              <div className="mt-3 pb-1">
                Already have an account?{' '}
                <a className="text-underline" onClick={openLoginModal}>
                  Sign in
                </a>
              </div>
            )}

            <hr className="p-0" />

            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <label htmlFor="customer-first-name">First Name</label>
                  <input
                    ref={register({ required: 'First name is required' })}
                    required
                    autoComplete="cc-given-name"
                    className="form-control"
                    id="customer-first-name"
                    name="firstName"
                    placeholder="Enter your first name..."
                    type="text"
                  />
                  <ErrorMessage as={FormErrors} errors={errors} name="firstName" />
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group">
                  <label htmlFor="customer-last-name">Last Name</label>
                  <input
                    ref={register({ required: 'Last name is required' })}
                    required
                    autoComplete="cc-family-name"
                    className="form-control"
                    id="customer-last-name"
                    name="lastName"
                    placeholder="Enter your last name..."
                    type="text"
                  />
                  <ErrorMessage as={FormErrors} errors={errors} name="lastName" />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <label htmlFor="customer-email">Email</label>
                  <input
                    ref={register({
                      required: 'Email is required',
                      pattern: {
                        value: EMAIL_PATTERN,
                        message: 'Email incorrect',
                      },
                    })}
                    required
                    autoComplete="email"
                    className="form-control mb-0"
                    id="customer-email"
                    name="email"
                    placeholder="Enter your email..."
                    type="email"
                  />
                  <ErrorMessage as={FormErrors} errors={errors} name="email" />
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group">
                  <label htmlFor="customer-phone">Phone</label>
                  <Controller
                    control={control}
                    name="phone"
                    render={(props) => (
                      <PhoneInput
                        {...props}
                        required
                        addInternationalOption={false}
                        autoComplete="tel"
                        countryOptionsOrder={locationCountryIsoCodes}
                        defaultCountry={mainCountryLocationIsoCode}
                        id="customer-phone"
                        numberInputProps={{ className: 'form-control' }}
                      />
                    )}
                    rules={{ required: 'Phone is required' }}
                  />
                  <ErrorMessage as={FormErrors} errors={errors} name="phone" />
                </div>
              </div>
            </div>

            <div className="custom-control / custom-checkbox">
              <input
                ref={register}
                defaultChecked
                className="custom-control-input"
                id="customer-subscribed"
                name="subscribed"
                type="checkbox"
              />
              <label className="custom-control-label" htmlFor="customer-subscribed">
                Keep me up to date on news and exclusive offers via email
              </label>
            </div>

            {currentLocation.canSubscribeViaSms ? (
              <div className="custom-control / custom-checkbox">
                <input
                  ref={register}
                  className="custom-control-input"
                  id="customer-subscribed-sms"
                  name="subscribedSms"
                  type="checkbox"
                />
                <label className="custom-control-label" htmlFor="customer-subscribed-sms">
                  Keep me up to date on news and exclusive offers via text
                </label>
              </div>
            ) : null}

            {isDisabledLogin ? null : (
              <>
                <div className="row">
                  {isOptionalLogin ? (
                    <div className="col-md-12">
                      <div className="form-group mb-1">
                        <div className="custom-control / custom-checkbox">
                          <input
                            checked={wantPassword}
                            className="custom-control-input"
                            id="want-password"
                            type="checkbox"
                            onChange={toggleWantPassword}
                          />
                          <label className="custom-control-label" htmlFor="want-password">
                            Create an account for faster checkout
                          </label>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="col-md-12">
                      <hr />
                      Accounts are required to checkout. Please create a password
                    </div>
                  )}
                </div>

                <div className="row" hidden={!(isRequiredLogin || wantPassword)}>
                  <div className="col-md-6">
                    <div className="form-group mb-0">
                      <input
                        ref={register({
                          required: {
                            message: 'Password is required',
                            value: isRequiredLogin || wantPassword,
                          },
                          minLength: {
                            message: 'Password min length is 6',
                            value: 6,
                          },
                        })}
                        autoComplete="new-password"
                        className="form-control"
                        id="customer-password"
                        name="password"
                        placeholder="Password"
                        type="password"
                      />
                    </div>
                  </div>
                  <div className="col-md-6 d-flex align-items-center">
                    <div className="mb-0">
                      <ErrorMessage as={FormErrors} errors={errors} name="password" />
                    </div>
                  </div>
                </div>
              </>
            )}
          </form>
        ) : (
          <div className="my-1">
            {customer?.firstName} {customer?.lastName}
            <div className="mt-1">{customer?.email}</div>
          </div>
        )}
      </div>
      {isActive ? <NextStepBtn loading={loading || checkouting} {...{ isLastStep }} onSubmit={onSubmit} /> : null}
      {loginModalVisibility ? (
        <LoginModal closeModal={closeLoginModal} openForgotPasswordModal={openForgotPasswordModal} />
      ) : null}
      {forgotPasswordModalVisibility ? <ForgotPasswordModal closeModal={closeForgotPasswordModal} /> : null}
    </FormProvider>
  )
}

export default memo(CustomerFieldsGroup)
